import { Input, Textarea } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris";
import React from "react";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { CustomTextFormFieldProps } from "interfaces";

/**
 * A FormField that provides a text input
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 * @param {string} errorText - Error message to display
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} placeholder - Placeholder text for the date input
 * @param {boolean} disabled - Whether the input is disabled
 * @param {CustomTextFormFieldVariant} variant - The variant representing if its a normal input or a text area
 * @param {string} [testId] - Test identifier for the component
 * @param {number} rows = The rows size for the text area
 * @param {function} onEnter - Function to be called when the user presses the enter key
 * @param {InputProps.Type} [type] - The type of the input
 *
 * @returns {JSX.Element} A FormField component with a Select
 */
const CustomTextFormField = ({
    label,
    value,
    setValue,
    placeholder,
    disabled = false,
    variant = "input",
    errorText,
    warningText,
    description,
    info,
    testId,
    rows,
    type,
    onEnter,
    touchpoint,
}: CustomTextFormFieldProps) => {
    let selectedInput: React.ReactNode;

    switch (variant) {
        case "textArea":
            selectedInput = (
                <Textarea
                    onChange={({ detail }) => setValue(detail.value)}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={rows}
                    onKeyDown={onEnter ? ({ detail }) => detail.key === "Enter" && onEnter() : undefined}
                    touchpoint={touchpoint}
                />
            );
            break;
        default:
            selectedInput = (
                <Input
                    type={type}
                    onChange={({ detail }) => setValue(detail.value)}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onKeyDown={onEnter ? ({ detail }) => detail.key === "Enter" && onEnter() : undefined}
                    touchpoint={touchpoint}
                />
            );
    }

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={selectedInput}
        />
    );
};

export default CustomTextFormField;
